import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

const StyledTableHead = styled(TableHead)(() => ({
  th: {
    whiteSpace: "nowrap",
    background: "#fff",
    color: "#4281AB",
    paddingTop: "6px",
    paddingBottom: "8px",
    fontFamily: "Open Sans",
    fontSize: "12px",
    fontWeight: "600",
    borderBottom: "1px solid #b2cede",
  },
}));

const StyledTableBody = styled(TableBody)(() => ({
  td: {
    color: "#000",
    paddingTop: "5px",
    paddingBottom: "5px",
    fontFamily: "Open Sans",
    fontSize: "12px",
    fontWeight: "400",
    borderBottom: "none",
    verticalAlign: "top",
  },
  tr: {
    background: "#fff",
    "&:nth-of-type(odd)": { background: "#EFF5F8" },
  },
}));

const StyledPaper = styled(Paper)(() => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  border: "1px solid #b2cede",
  borderRadius: "6px",
}));

const HEAD_CELLS = [
  { id: "status", label: "Status" },
  { id: "date", label: "Date / Time" },
  { id: "performedBy", label: "Performed By" },
  { id: "action", label: "Action" },
  { id: "note", label: "Note" },
];

// History CreateDate is stored in UTC (GETUTCDATE / Azure GETDATE); parse as UTC and render in
// the viewer's local timezone so the timestamp matches when the change actually happened for them.
const fmt = (v) => (v ? moment.utc(v).local().format("MM/DD/YYYY h:mm A") : "—");

/**
 * Shared grid dialog for service status history and service billing history —
 * a single table style (matches the Plan of Care event log) for both.
 *
 * Props:
 *   open, onClose, title
 *   rows        – history rows [{ status, statusDate, createdBy, note,
 *                                 billingHistoryAction | statusHistoryAction | action }]
 *   isLoading, isError
 *   emptyText   – message when there are no rows
 *   headerRight – optional node rendered in the header (e.g. a status selector)
 */
export const ServiceHistoryDialog = ({
  open,
  onClose,
  title = "History",
  rows,
  isLoading,
  isError,
  emptyText = "No history available.",
  headerRight = null,
}) => {
  const list = Array.isArray(rows) ? rows : [];

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ flex: 1 }}>{title}</Box>
        {headerRight}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {isLoading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={30} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : isError ? (
          <Typography sx={{ fontSize: "12px", color: "error.main" }}>Failed to load history.</Typography>
        ) : list.length === 0 ? (
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", py: 3, textAlign: "center", border: "1px dashed #b2cede", borderRadius: "6px" }}
          >
            {emptyText}
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: "11px", color: "text.secondary", mb: 1 }}>
              {list.length} entr{list.length !== 1 ? "ies" : "y"}
            </Typography>
            <TableContainer component={StyledPaper} sx={{ maxHeight: 420, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    {HEAD_CELLS.map((c) => (
                      <TableCell key={c.id}>{c.label}</TableCell>
                    ))}
                  </TableRow>
                </StyledTableHead>
                <StyledTableBody>
                  {list.map((row, idx) => (
                    <TableRow key={row.id ?? idx}>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>{row.status ?? "—"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(row.statusDate)}</TableCell>
                      <TableCell>{row.createdBy || "—"}</TableCell>
                      <TableCell>{row.billingHistoryAction ?? row.statusHistoryAction ?? row.action ?? "—"}</TableCell>
                      <TableCell>{row.note || "—"}</TableCell>
                    </TableRow>
                  ))}
                </StyledTableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};
